/**
 * The true-up's dialog, drawn from core's `useTrueUpDialog`: a modal over the
 * editor that says it is working while the true-up runs, then shows what it
 * changed and stays until dismissed.
 */

import { css } from '@emotion/react';
import { TRUE_UP_DIALOG_TEXT, type TrueUpDialog } from '@kaiizen/kai-floor-plan-editor-core';
import { ink, menuShadow, midtone, monoLabel, white } from '../../styles';
import { BTN_ROW_STYLE, BTN_STYLE } from '../panel';

const BACKDROP_STYLE = css`
  label: TRUE_UP_BACKDROP_STYLE;
  position: fixed; inset: 0; z-index: 60;
  display: flex; align-items: center; justify-content: center;
  background: rgba(26, 26, 26, 0.35);
`;

const CARD_STYLE = css`
  label: TRUE_UP_CARD_STYLE;
  box-sizing: border-box; width: 360px; max-width: calc(100vw - 32px); max-height: calc(100vh - 32px); overflow-y: auto;
  padding: 18px 20px; background: ${white}; border: 1px solid ${ink}; box-shadow: ${menuShadow};
  color: ${ink}; font-size: 13px; line-height: 1.4;
  h2 { ${monoLabel} font-size: 11px; letter-spacing: 0.14em; color: ${midtone}; margin: 0 0 12px; }
  p { margin: 0 0 8px; }
  ul { margin: 0 0 8px; padding-left: 18px; }
`;

export function TrueUpDialogView({ dialog }: { dialog: TrueUpDialog }): React.JSX.Element | null {
  if (dialog.phase === 'closed') return null;
  return (
    <div css={BACKDROP_STYLE}>
      <div css={CARD_STYLE} role='dialog' aria-modal='true' aria-label={TRUE_UP_DIALOG_TEXT.title}>
        <h2>{TRUE_UP_DIALOG_TEXT.title}</h2>
        {dialog.phase === 'working'
          ? <p>{TRUE_UP_DIALOG_TEXT.working}</p>
          : (
            <>
              <p data-testid='true-up-result'>{dialog.summary}</p>
              {dialog.changes.length > 0 && (
                <ul data-testid='true-up-changes'>
                  {dialog.changes.map(change => <li key={change}>{change}</li>)}
                </ul>
              )}
              <div css={BTN_ROW_STYLE}>
                <button type='button' css={BTN_STYLE} onClick={dialog.dismiss}>{TRUE_UP_DIALOG_TEXT.dismiss}</button>
              </div>
            </>
          )}
      </div>
    </div>
  );
}
